import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { realtimeService } from '../../../services/realtimeService';
import { performanceMetricsService } from '../../../services/performanceMetricsService';

const PlatformHealth = () => {
  const navigate = useNavigate();
  const [systemMetrics, setSystemMetrics] = useState([
    {
      label: "System Uptime",
      value: "99.97%",
      status: "healthy",
      icon: "Activity",
      description: "Last 30 days"
    },
    {
      label: "API Response Time",
      value: "142ms",
      status: "healthy",
      icon: "Zap",
      description: "Average latency"
    },
    {
      label: "Error Rate",
      value: "0.03%",
      status: "healthy",
      icon: "AlertCircle",
      description: "Last 24 hours"
    },
    {
      label: "Active Sessions",
      value: "47",
      status: "healthy",
      icon: "Users",
      description: "Currently in progress"
    }
  ]);
  const [serviceStatus, setServiceStatus] = useState([]);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServiceHealth();

    // Subscribe to real-time performance_metrics changes
    const subscription = realtimeService?.subscribeToPerformanceMetrics((payload) => {
      const { new: newMetric } = payload;

      // Update system metrics based on new performance data
      if (newMetric?.metric_type === 'api_response') {
        setSystemMetrics((prev) =>
          prev?.map((metric) =>
            metric?.label === 'API Response Time'
              ? { 
                  ...metric, 
                  value: `${Math.round(newMetric?.metric_value)}ms`,
                  status: newMetric?.metric_value < 200 ? 'healthy' : newMetric?.metric_value < 500 ? 'warning' : 'critical'
                }
              : metric
          )
        );
      } else if (newMetric?.metric_type === 'active_connections') {
        setSystemMetrics((prev) =>
          prev?.map((metric) =>
            metric?.label === 'Active Sessions'
              ? { ...metric, value: String(Math.round(newMetric?.metric_value)) }
              : metric
          )
        );
      }

      // Refresh service status when metrics change
      loadServiceHealth();
    });

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadServiceHealth = async () => {
    try {
      const healthData = await performanceMetricsService?.getServiceHealth();
      setServiceStatus(healthData);

      const alertsData = await performanceMetricsService?.detectAlerts();
      // Convert alerts to incidents format
      const incidents = alertsData?.map((alert, index) => ({
        id: index + 1,
        date: new Date(alert?.timestamp)?.toISOString()?.split('T')?.[0],
        title: alert?.title,
        severity: alert?.severity,
        status: alert?.status,
        description: alert?.description,
        affectedUsers: 0 // This would need additional tracking
      }));
      setRecentIncidents(incidents);

      setLoading(false);
    } catch (error) {
      console.error('Error loading service health:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      operational: "text-success",
      degraded: "text-warning",
      outage: "text-error",
      healthy: "text-success"
    };
    return colors?.[status] || "text-muted-foreground";
  };

  const getStatusIcon = (status) => {
    const icons = {
      operational: "CheckCircle2",
      degraded: "AlertTriangle",
      outage: "XCircle",
      healthy: "CheckCircle2"
    };
    return icons?.[status] || "Info";
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      low: "bg-muted text-muted-foreground",
      medium: "bg-warning/10 text-warning",
      high: "bg-error/10 text-error",
      critical: "bg-error text-error-foreground"
    };
    return badges?.[severity] || badges?.low;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {systemMetrics?.map((metric, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-primary/10`}>
                <Icon name={metric?.icon} size={24} className="text-primary" />
              </div>
              <Icon 
                name={getStatusIcon(metric?.status)} 
                size={20} 
                className={getStatusColor(metric?.status)} 
              />
            </div>
            <p className="text-2xl font-bold mb-1 text-data">{metric?.value}</p>
            <p className="text-sm font-medium mb-1">{metric?.label}</p>
            <p className="text-xs text-muted-foreground">{metric?.description}</p>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Icon name="Server" size={24} className="text-primary" />
            <h3 className="text-lg font-semibold">Service Status</h3>
          </div>
          <Button
            variant="outline"
            iconName="ExternalLink"
            iconPosition="right"
            onClick={() => window.open('https://status.mindconnect.com', '_blank')}
          >
            Status Page
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Service</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Uptime</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Response Time</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Incident</th>
              </tr>
            </thead>
            <tbody>
              {serviceStatus?.map((service, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <span className="text-sm font-medium">{service?.service}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Icon 
                        name={getStatusIcon(service?.status)} 
                        size={16} 
                        className={getStatusColor(service?.status)} 
                      />
                      <span className={`text-sm font-medium ${getStatusColor(service?.status)}`}>
                        {service?.status?.charAt(0)?.toUpperCase() + service?.status?.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-data">{service?.uptime}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-data">{service?.responseTime}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-muted-foreground">{service?.lastIncident}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="AlertTriangle" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold">Recent Incidents</h3>
            <p className="text-sm text-muted-foreground">{recentIncidents?.length} incidents in last 30 days</p>
          </div>
        </div>

        <div className="space-y-4">
          {recentIncidents?.map((incident) => (
            <div key={incident?.id} className="border border-border rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-sm font-semibold">{incident?.title}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(incident?.severity)}`}>
                      {incident?.severity?.toUpperCase()}
                    </span>
                    <span className={`text-xs font-medium ${incident?.status === 'resolved' ? 'text-success' : 'text-warning'}`}>
                      {incident?.status?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{incident?.description}</p>
                  <p className="text-xs text-muted-foreground">
                    Affected users: {incident?.affectedUsers} • {incident?.date}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/system-performance-dashboard', { state: { incidentId: incident?.id } })}>
                  View Details
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <Icon name="Info" size={24} className="text-primary mt-0.5" />
          <div className="flex-1">
            <h4 className="text-base font-semibold mb-2">Monitoring & Observability</h4>
            <p className="text-sm text-muted-foreground mb-4">
              All platform services are continuously monitored with automated health checks, error logging, and performance tracking. Critical alerts are sent to on-call engineers immediately.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" iconName="BarChart3" iconPosition="left">
                View Metrics
              </Button>
              <Button variant="outline" size="sm" iconName="Bell" iconPosition="left">
                Alert Settings
              </Button>
              <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
                Incident Reports
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformHealth;